import React, { useState } from "react";
import { X, ChevronDown } from "lucide-react";
import Cookies from "js-cookie";

const roleBadgeClass = (role = "") => {
  const r = role.toLowerCase();
  if (r === "admin") return "badge badge--admin";
  if (r === "researcher") return "badge badge--researcher";
  if (r === "collaborator") return "badge badge--collaborator";
  return "badge badge--default";
};

const OrgDirectoryModal = ({
  onClose,
  orgs,
  loading,
  pagination,
  onPageChange,
  selectedOrg,
  onSelectOrg,
}) => {
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [membersData, setMembersData] = useState({});
  const [membersLoading, setMembersLoading] = useState({});

  const fetchMembers = async (orgId) => {
    if (membersData[orgId]) return;
    setMembersLoading((p) => ({ ...p, [orgId]: true }));
    try {
      const token = Cookies.get("token");
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/superadmin/orgs/${orgId}/members?limit=10`,
        { headers: { token, "Content-Type": "application/json" } }
      );
      const json = await res.json();
      setMembersData((p) => ({ ...p, [orgId]: json }));
    } catch {
      setMembersData((p) => ({ ...p, [orgId]: { error: true } }));
    } finally {
      setMembersLoading((p) => ({ ...p, [orgId]: false }));
    }
  };

  const toggleExpand = (orgId) => {
    if (expandedOrg === orgId) {
      setExpandedOrg(null);
    } else {
      setExpandedOrg(orgId);
      fetchMembers(orgId);
    }
  };

  const handleRowClick = (org) => {
    // Select the org for the project table (syncs dashboard)
    onSelectOrg(org);
  };

  const handleChevronClick = (e, orgId) => {
    e.stopPropagation(); // don't fire row-select
    toggleExpand(orgId);
  };

  return (
    <div
      className="modal-overlay modal-overlay--org"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal modal--org modal--org-wide">

        {/* Header */}
        <div className="modal__header">
          <div>
            <h2 className="modal__title">Organization & Admin Directory</h2>
            {selectedOrg && (
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "#94a3b8" }}>
                Selected: <strong style={{ color: "#fff" }}>{selectedOrg.org_name}</strong>
                {" — projects will update in the dashboard"}
              </p>
            )}
          </div>
          <button className="modal__close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Table */}
        <div className="modal__body">
          <table className="data-table data-table--modal data-table--clickable">
            <colgroup>
              <col style={{ width: 32 }} />
              <col style={{ width: "28%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                {["Org Name", "Org Id", "Projects", "Running", "Failed", "Members"].map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-loading">Loading…</td>
                </tr>
              ) : (
                orgs.map((org) => {
                  const isExpanded = expandedOrg === org._id;
                  const isSelected = selectedOrg?._id === org._id;
                  const members = membersData[org._id];

                  return (
                    <React.Fragment key={org._id}>
                      <tr
                        onClick={() => handleRowClick(org)}
                        className={[
                          isExpanded ? "org-row--expanded" : "",
                          isSelected ? "org-row--selected" : "",
                        ].join(" ")}
                        style={{ cursor: "pointer" }}
                      >
                        {/* Chevron cell — expands members, doesn't select */}
                        <td
                          onClick={(e) => handleChevronClick(e, org._id)}
                          style={{ cursor: "pointer", paddingRight: 0 }}
                        >
                          <ChevronDown
                            size={14}
                            color="#666"
                            className={`org-row__chevron ${isExpanded ? "org-row__chevron--open" : "org-row__chevron--closed"}`}
                          />
                        </td>
                        <td>
                          <span className="org-name-cell">
                            {org.org_name}
                          </span>
                        </td>
                        <td className="td--mono">{org._id?.toString().slice(-8).toUpperCase()}</td>
                        <td>{org.projectCount ?? "—"}</td>
                        <td>
                          <span style={{ color: "#4a9eff", fontWeight: 500 }}>
                            {org.runningProjectCount ?? "—"}
                          </span>
                        </td>
                        <td>
                          <span style={{ color: "#f87171", fontWeight: 500 }}>
                            {org.failedProjectCount ?? "—"}
                          </span>
                        </td>
                        <td>{org.memberCount ?? "—"}</td>
                      </tr>

                      {/* Expanded members panel */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={7} className="members-panel">
                            {membersLoading[org._id] ? (
                              <div className="text-empty" style={{ paddingTop: 20 }}>
                                Loading members…
                              </div>
                            ) : members?.error ? (
                              <div className="text-error">Failed to load members</div>
                            ) : (
                              <div className="members-panel__inner">
                                <div className="members-panel__header">
                                  <span className="members-panel__label">Organization Members</span>
                                  <span className="members-panel__count">
                                    {members?.data?.length ?? 0} of {members?.pagination?.total ?? 0} shown
                                  </span>
                                </div>
                                <div className="members-grid">
                                  {(members?.data ?? []).slice(0, 12).map((m) => (
                                    <div key={m._id} className="member-card">
                                      <div className="member-card__name">
                                        {m.first_name || m.email?.split("@")[0] || "User"}
                                      </div>
                                      <div className="member-card__email">{m.email}</div>
                                      <span className={roleBadgeClass(m.role)}>
                                        {m.role || "Member"}
                                      </span>
                                    </div>
                                  ))}
                                  {(members?.pagination?.total ?? 0) > 12 && (
                                    <div className="member-card__more">
                                      View all {members?.pagination?.total} members →
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination?.totalPages > 1 && (
          <div className="pagination">
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => onPageChange(p)}
                className={`pagination__btn ${pagination.page === p ? "pagination__btn--active" : "pagination__btn--inactive"}`}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrgDirectoryModal;